import React, { useState } from 'react';
import { fieldTypeLabel } from '../fieldTypes';
import { Icon, sourceIconName } from './icons';
import {
  DETAILS,
  TEXT_SOURCE,
  detailSource,
  fieldSource,
  filterFieldGroups,
  sourceKey,
  sourceTitle,
} from './templateLayout';

const matchesQuery = (text, query) => text.toLowerCase().includes(query.trim().toLowerCase());

const Heading = ({ children }) => (
  <div className="px-2.5 pt-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
    {children}
  </div>
);

// One thing that can go in a box. `extraProps` lets the palette make it
// draggable; a click picks it either way, which is also what Enter and Space
// do from the keyboard.
const SourceItem = ({ source, subtitle, placed, onPick, extraProps, draggable }) => {
  const title = sourceTitle(source);
  return (
    <button
      type="button"
      onClick={() => onPick(source)}
      title={source.kind === 'field' && source.field.label ? source.field.label : title}
      {...extraProps}
      className={`group/source w-full flex items-center gap-2.5 bg-transparent hover:bg-purple-500/15 focus-visible:bg-purple-500/15 text-left px-2.5 py-1.5 rounded-lg transition-colors select-none ${
        draggable ? 'cursor-grab active:cursor-grabbing touch-none' : ''
      }`}
    >
      <span className="flex items-center justify-center w-7 h-7 shrink-0 rounded-md bg-purple-500/15 text-purple-300 group-hover/source:bg-purple-500/30 group-hover/source:text-purple-100 transition-colors">
        <Icon name={sourceIconName(source)} className="w-4 h-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-gray-100 truncate">{title}</span>
        {subtitle && <span className="block text-xs text-gray-500 truncate">{subtitle}</span>}
      </span>
      {placed > 0 && (
        <span
          className="shrink-0 inline-flex items-center gap-0.5 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[11px] font-medium text-emerald-300"
          title={placed === 1 ? 'Already on the page' : `On the page ${placed} times`}
        >
          <Icon name="check" className="w-3 h-3" strokeWidth={2.5} />
          {placed > 1 ? placed : ''}
        </span>
      )}
    </button>
  );
};

// Everything that can fill a box, searchable: the form's questions under their
// section headings, the details every submission carries, and text of your
// own. The palette lists it to drag from; the picker lists it to choose from,
// so the two can never offer different things.
const SourceList = ({ formName, groups, placed, onPick, itemProps, draggable = false, autoFocus = false }) => {
  const [query, setQuery] = useState('');
  const shownGroups = filterFieldGroups(groups, query);
  const shownDetails = DETAILS.filter((detail) => matchesQuery(detail.label, query));
  const showText = matchesQuery('Text heading label note your own', query);
  const searching = query.trim() !== '';
  const nothing = searching && shownGroups.length === 0 && shownDetails.length === 0 && !showText;

  const item = (source, subtitle) => (
    <SourceItem
      key={sourceKey(source)}
      source={source}
      subtitle={subtitle}
      placed={placed?.get(sourceKey(source)) || 0}
      onPick={onPick}
      extraProps={itemProps ? itemProps(source) : undefined}
      draggable={draggable}
    />
  );

  return (
    <div>
      <div className="relative px-1">
        <Icon name="search" className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find a question…"
          aria-label="Find a question"
          autoFocus={autoFocus}
          className="w-full rounded-lg border border-gray-600 bg-gray-700/50 py-2 pl-9 pr-3 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      {nothing && <p className="px-2.5 pt-4 text-sm text-gray-400">Nothing matches “{query.trim()}”.</p>}

      {(!searching || shownGroups.length > 0) && (
        <>
          <Heading>{formName ? `Questions on ${formName}` : 'Questions'}</Heading>
          {groups.length === 0 && (
            <p className="px-2.5 pb-2 text-xs text-gray-500">
              {formName ? 'This form has no questions yet.' : 'Choose a form to list its questions.'}
            </p>
          )}
          {shownGroups.map((group) => (
            <div key={group.key}>
              {group.title && (
                <div className="mx-2.5 mt-2 mb-1 border-l-2 border-purple-500/40 pl-2 text-xs font-medium text-purple-200/80 truncate">
                  {group.title}
                </div>
              )}
              {group.fields.map((field) => item(fieldSource(field), fieldTypeLabel(field.type)))}
            </div>
          ))}
        </>
      )}

      {shownDetails.length > 0 && (
        <>
          <Heading>Submission details</Heading>
          {shownDetails.map((detail) => item(detailSource(detail.value), 'Every submission has this'))}
        </>
      )}

      {showText && (
        <>
          <Heading>Your own text</Heading>
          {item(TEXT_SOURCE, 'A heading, label or note')}
        </>
      )}
    </div>
  );
};

export default SourceList;
